import { useCallback, useEffect, useState } from 'react';

import { UserAuth } from '@/authentication/AuthContext.jsx';
import { getAllRoles } from '@/services/admin-settings/RoleService.jsx';
import { prettyRole } from '@/utils/roleLabels.js';

/**
 * Resolves role ids to their human display names from the Role Registry — the
 * single source of truth (rbac-config.json seeds the DB; `GET /role/org`
 * returns `{ roleId, roleName }` for every role, system + org-custom).
 *
 * Returns:
 *   - `roleNames`: the roleId → roleName map (empty until the fetch resolves).
 *   - `roleLabel(roleId)`: the display name, falling back to a title-cased label
 *     (see prettyRole) when the registry has no entry — so a raw enum is never
 *     shown, even before the catalogue loads or if the fetch fails.
 *
 * Mirrors the existing Profile-page logic; share this instead of hardcoding a
 * role-name map per surface.
 */
export default function useRoleNames() {
    const { user } = UserAuth();
    const [roleNames, setRoleNames] = useState({});

    useEffect(() => {
        if (!user?.accessToken) return undefined;
        let alive = true;
        (async () => {
            try {
                const res = await getAllRoles({ accessToken: user.accessToken });
                if (!alive) return;
                const map = {};
                (res?.roles || []).forEach((r) => {
                    if (r?.roleId && r?.roleName) map[r.roleId] = r.roleName;
                });
                setRoleNames(map);
            } catch (err) {
                console.warn('useRoleNames: failed to load role catalogue; using fallback labels', err);
            }
        })();
        return () => { alive = false; };
    }, [user?.accessToken]);

    // Stable identity (changes only when the map loads) so consumers can safely
    // list `roleLabel` in useMemo / useCallback dependency arrays.
    const roleLabel = useCallback(
        (roleId) => roleNames[roleId] || prettyRole(roleId),
        [roleNames],
    );
    return { roleNames, roleLabel };
}
